import { CaretDown, Check } from "@phosphor-icons/react";
import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";

export interface CustomSelectOption<Value extends string> {
  value: Value;
  label: string;
}

interface CustomSelectProps<Value extends string> {
  icon: ReactNode;
  label: string;
  onChange: (value: Value) => void;
  options: readonly CustomSelectOption<Value>[];
  value: Value;
}

export function CustomSelect<Value extends string>({ icon, label, onChange, options, value }: CustomSelectProps<Value>) {
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef(new Map<Value, HTMLButtonElement>());
  const typeahead = useRef({ query: "", updatedAt: 0 });
  const selected = options.find((option) => option.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const focusFrame = requestAnimationFrame(() => optionRefs.current.get(value)?.focus());
    const closeOnOutsidePointer = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnOutsideFocus = (event: FocusEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    document.addEventListener("focusin", closeOnOutsideFocus, true);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
      document.removeEventListener("focusin", closeOnOutsideFocus, true);
    };
  }, [open, value]);

  if (!selected) return null;

  const focusOption = (index: number): void => {
    const option = options[(index + options.length) % options.length];
    if (option) optionRefs.current.get(option.value)?.focus();
  };

  const selectOption = (nextValue: Value): void => {
    onChange(nextValue);
    setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const handleOptionKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      requestAnimationFrame(() => triggerRef.current?.focus());
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(index + 1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      focusOption(index - 1);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      focusOption(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      focusOption(options.length - 1);
      return;
    }
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;

    const now = Date.now();
    const query = now - typeahead.current.updatedAt > 600
      ? event.key.toLocaleLowerCase()
      : `${typeahead.current.query}${event.key.toLocaleLowerCase()}`;
    typeahead.current = { query, updatedAt: now };
    const matchOffset = Array.from({ length: options.length }, (_, offset) => (index + offset + 1) % options.length)
      .find((optionIndex) => options[optionIndex]?.label.toLocaleLowerCase().startsWith(query));
    if (matchOffset !== undefined) {
      event.preventDefault();
      focusOption(matchOffset);
    }
  };

  return (
    <div ref={rootRef} className="custom-select">
      <span className="custom-select__label" id={labelId}>{icon}{label}</span>
      <button
        ref={triggerRef}
        type="button"
        className="custom-select__trigger"
        aria-controls={open ? listboxId : undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`${label}: ${selected.label}`}
        data-state={open ? "open" : "closed"}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && open) {
            event.preventDefault();
            setOpen(false);
            requestAnimationFrame(() => triggerRef.current?.focus());
            return;
          }
          if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
          event.preventDefault();
          setOpen(true);
        }}
      >
        <span>{selected.label}</span>
        <CaretDown aria-hidden="true" weight="bold" />
      </button>
      {open && (
        <div className="custom-select__content" id={listboxId} role="listbox" aria-labelledby={labelId}>
          {options.map((option, index) => (
            <button
              key={option.value}
              ref={(node) => {
                if (node) optionRefs.current.set(option.value, node);
                else optionRefs.current.delete(option.value);
              }}
              type="button"
              className="custom-select__option"
              role="option"
              aria-selected={option.value === value}
              tabIndex={option.value === value ? 0 : -1}
              onClick={() => selectOption(option.value)}
              onKeyDown={(event) => handleOptionKeyDown(event, index)}
            >
              <span>{option.label}</span>
              {option.value === value && <Check className="custom-select__check" aria-hidden="true" weight="bold" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
